import { LitElement } from 'lit';
import * as THREE from 'three';

function parseArgs(s, fallback) {
  if (!s) return fallback;
  const parts = String(s).trim().split(/\s+/).map(Number);
  if (parts.some(Number.isNaN)) return fallback;
  return parts;
}

class GeometryElement extends LitElement {
  static properties = {
    args: { type: String },
  };

  constructor() {
    super();
    this.args = '';
    this.geometry = null;
  }

  createGeometry() {
    throw new Error('createGeometry() must be implemented');
  }

  connectedCallback() {
    super.connectedCallback();
    this.geometry = this.createGeometry();
    this.dispatchEvent(new CustomEvent('geometry-changed', { bubbles: true, composed: true }));
  }

  updated(changed) {
    if (changed.has('args') && this.geometry) {
      this.geometry.dispose();
      this.geometry = this.createGeometry();
      this.dispatchEvent(new CustomEvent('geometry-changed', { bubbles: true, composed: true }));
    }
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    if (this.geometry) this.geometry.dispose();
  }

  createRenderRoot() {
    return this;
  }
}

export class ThreeBoxGeometry extends GeometryElement {
  createGeometry() {
    const [w = 1, h = 1, d = 1] = parseArgs(this.args, [1, 1, 1]);
    return new THREE.BoxGeometry(w, h, d);
  }
}

export class ThreeSphereGeometry extends GeometryElement {
  createGeometry() {
    const [r = 1, ws = 32, hs = 16] = parseArgs(this.args, [1, 32, 16]);
    return new THREE.SphereGeometry(r, ws, hs);
  }
}

export class ThreeCylinderGeometry extends GeometryElement {
  createGeometry() {
    const [rt = 1, rb = 1, h = 1, rs = 32] = parseArgs(this.args, [1, 1, 1, 32]);
    return new THREE.CylinderGeometry(rt, rb, h, rs);
  }
}

export class ThreePlaneGeometry extends GeometryElement {
  createGeometry() {
    const [w = 1, h = 1, ws = 1, hs = 1] = parseArgs(this.args, [1, 1, 1, 1]);
    return new THREE.PlaneGeometry(w, h, ws, hs);
  }
}

export class ThreeConeGeometry extends GeometryElement {
  createGeometry() {
    const [r = 1, h = 1, rs = 32] = parseArgs(this.args, [1, 1, 32]);
    return new THREE.ConeGeometry(r, h, rs);
  }
}

export class ThreeCapsuleGeometry extends GeometryElement {
  createGeometry() {
    const [r = 0.5, l = 1, cs = 4, rs = 8] = parseArgs(this.args, [0.5, 1, 4, 8]);
    return new THREE.CapsuleGeometry(r, l, cs, rs);
  }
}

export class ThreeTorusGeometry extends GeometryElement {
  createGeometry() {
    const [r = 1, t = 0.4, rs = 12, ts = 48] = parseArgs(this.args, [1, 0.4, 12, 48]);
    return new THREE.TorusGeometry(r, t, rs, ts);
  }
}

export class ThreeCircleGeometry extends GeometryElement {
  createGeometry() {
    const [r = 1, s = 32] = parseArgs(this.args, [1, 32]);
    return new THREE.CircleGeometry(r, s);
  }
}

customElements.define('three-box-geometry', ThreeBoxGeometry);
customElements.define('three-sphere-geometry', ThreeSphereGeometry);
customElements.define('three-cylinder-geometry', ThreeCylinderGeometry);
customElements.define('three-plane-geometry', ThreePlaneGeometry);
customElements.define('three-cone-geometry', ThreeConeGeometry);
customElements.define('three-capsule-geometry', ThreeCapsuleGeometry);
customElements.define('three-torus-geometry', ThreeTorusGeometry);
customElements.define('three-circle-geometry', ThreeCircleGeometry);
